import React, { FC, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Stack,
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { ListYourVenueLink } from './ListYourVenueLink';
import { VenueSearch } from './VenueSearch';
import { FindAVenueButton } from './FindAVenueButton';
import { MarketplaceHomePageLink } from '../../../components/MarketplaceHomePageLink';
import { Search } from 'react-feather';

const DesktopToolbarContent = ({
  searchMode,
  handleCloseSearch,
}: {
  searchMode: boolean;
  handleCloseSearch: () => void;
}) => {
  return (
    <>
      <Stack
        direction="row"
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {!searchMode && <MarketplaceHomePageLink />}

        <Stack direction="row" gap="40px" alignItems="center">
          <ListYourVenueLink />
          <FindAVenueButton />
        </Stack>
      </Stack>

      {searchMode && (
        <IconButton
          onClick={handleCloseSearch}
          sx={{
            position: 'absolute',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        >
          <CloseIcon />
        </IconButton>
      )}

      <Box
        sx={{
          width: '300px',
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      >
        <VenueSearch />
      </Box>
    </>
  );
};

const MobileToolbarContent = ({
  searchMode,
  handleCloseSearch,
  setMobileMenuOpen,
}: {
  searchMode: boolean;
  handleCloseSearch: () => void;
  setMobileMenuOpen: (open: boolean) => void;
}) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{ width: '100%' }}
    >
      {searchMode ? (
        <>
          <IconButton onClick={handleCloseSearch}>
            <CloseIcon />
          </IconButton>
          <VenueSearch />
        </>
      ) : (
        <>
          <MarketplaceHomePageLink />
          <IconButton
            onClick={() => setMobileMenuOpen(true)}
            sx={{ marginLeft: 'auto' }}
          >
            <MenuIcon sx={{ color: 'black' }} />
          </IconButton>
        </>
      )}
    </Stack>
  );
};

const MobileMenu = ({
  mobileMenuOpen,
  setMobileMenuOpen,
  handleOpenSearch,
}: {
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  handleOpenSearch: () => void;
}) => {
  return (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
    >
      <Stack spacing={2} sx={{ width: 250, p: 2 }}>
        <Button onClick={handleOpenSearch} startIcon={<Search size={20} />}>
          Search Venue
        </Button>
        <ListYourVenueLink />
        <FindAVenueButton />
      </Stack>
    </Drawer>
  );
};

export const NavigationBar: FC = () => {
  const theme = useTheme();
  const mdMedia = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchMode, setSearchMode] = useState(false);

  const handleOpenSearch = () => {
    setMobileMenuOpen(false);

    setTimeout(() => {
      setSearchMode(true);
    }, 250);
  };

  const handleCloseSearch = () => {
    setSearchMode(false);
  };

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: '#fff',
        boxShadow: 'none',
        height: '60px',
      }}
    >
      <Toolbar sx={{ position: 'relative', height: '80px' }}>
        {!mdMedia ? (
          <DesktopToolbarContent
            searchMode={searchMode}
            handleCloseSearch={handleCloseSearch}
          />
        ) : (
          <MobileToolbarContent
            searchMode={searchMode}
            handleCloseSearch={handleCloseSearch}
            setMobileMenuOpen={setMobileMenuOpen}
          />
        )}
      </Toolbar>

      <MobileMenu
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        handleOpenSearch={handleOpenSearch}
      />
    </AppBar>
  );
};
